import React, { useState, useEffect, useCallback } from 'react'
import Sidebar from './components/Sidebar'
import ChatWindow from './components/ChatWindow'
import Loader from './components/Loader'
import ShowError from './components/ShowError'
import { conversationApi } from './api'
import { userApi } from './navixy'

const params = new URLSearchParams(window.location.search)
const navixySessionKey = params.get("session_key")

export default function App() {
  const [conversations, setConversations] = useState([])
  const [activeConvId, setActiveConvId] = useState(() => sessionStorage.getItem('pendingConvId'))
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [loadingConvs, setLoadingConvs] = useState(true)
  const [loadingUserInfo, setLoadingUserInfo] = useState(true)
  const [userInfo, setUserInfo] = useState(null)
  
  useEffect(() => {
    activeConvId ? sessionStorage.setItem('activeConvId', activeConvId)
                : sessionStorage.removeItem('activeConvId')
  }, [activeConvId])

  // Load user info on mount
  useEffect(() => {
    userApi.get_info(navixySessionKey)
      .then(setUserInfo)
      .catch(console.error)
      .finally(() => setLoadingUserInfo(false))
  }, [])

  // Load conversations
  useEffect(() => {
    if (!userInfo?.success) return

    conversationApi.list(userInfo.user_info.id)
      .then(setConversations)
      .catch(console.error)
      .finally(() => setLoadingConvs(false))    
  }, [userInfo])

  const activeConversation = conversations.find(c => c.id === activeConvId) || null

  // Create a new conversation
  const handleCreate = useCallback(async () => {
    try {
      const conv = await conversationApi.create(userInfo?.user_info.id, navixySessionKey, 'Nova poruka')
      setConversations(prev => [conv, ...prev])
      setActiveConvId(conv.id)
      return conv
    } catch (err) {
      console.error('Failed to create conversation', err)
      throw err
    }
  }, [loadingUserInfo])

  // Called when the first message is sent without an existing conversation
  const handleFirstMessage = useCallback(async (text) => {
    const conv = await handleCreate()
    return conv
  }, [handleCreate])

  // Delete a conversation
  const handleDelete = useCallback(async (id) => {
    await conversationApi.delete(id)
    setConversations(prev => prev.filter(c => c.id !== id))
    if (activeConvId === id) setActiveConvId(null)
  }, [activeConvId])

  // Rename a conversation
  const handleRename = useCallback(async (id, title) => {
    const updated = await conversationApi.rename(id, title)
    setConversations(prev => prev.map(c => c.id === id ? { ...c, title: updated.title } : c))
  }, [])

  // Refresh conversation list (e.g., after a message updates the title)
  const refreshConversations = useCallback(() => {
    conversationApi.list(userInfo?.user_info.id)
      .then(setConversations)
      .catch(console.error)
  }, [loadingUserInfo])

  // Refresh after messages change conversation title
  useEffect(() => {
    if (!activeConvId) return
    const interval = setInterval(refreshConversations, 5000)
    return () => clearInterval(interval)
  }, [activeConvId, refreshConversations])

  const isUserValid = userInfo?.success
  const isLoading = loadingUserInfo || (isUserValid && loadingConvs)

  if (isLoading) {
    return <Loader />
  }

  if (!isUserValid) {
    return <ShowError />
  }

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      background: 'var(--bg-main)',
    }}>
      <Sidebar
        conversations={conversations}
        activeId={activeConvId}
        onSelect={setActiveConvId}
        onCreate={handleCreate}
        onDelete={handleDelete}
        onRename={handleRename}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(v => !v)}
      />

      <ChatWindow
        userInfo={userInfo}
        conversation={activeConversation}
        onFirstMessage={handleFirstMessage}
      />
    </div>
  )
}
